/*
 * Jira Ticket:
 * Created Date: Mon, 1st Jun 2020, 15:44:11 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React from 'react';
import {
  Edit,
  Labeled,
  required,
  SelectArrayInput,
  SimpleForm,
  TextInput,
  useQueryWithStore,
  useTranslate,
  usePermissions,
  Title,
} from 'react-admin';
import { LinearProgress, FormHelperText } from '@material-ui/core';
import { localeCountryListInputs as styles } from 'styles';
import { AccessDenied } from 'Layout';
import { isSuperAdmin } from 'utils/roles';

const ErrorText = ({ classes, dataType, translate }) => {
  return (
    <FormHelperText className={classes.helperText}>
      {translate('error.region.errorGettingData', {
        dataType,
      })}
    </FormHelperText>
  );
};

const LoadingComponent = ({ label }) => {
  return (
    <Labeled label={label}>
      <LinearProgress />
    </Labeled>
  );
};

const RegionEdit = props => {
  const { permissions } = usePermissions();
  const classes = styles();
  const translate = useTranslate();

  const {
    loaded: countryLoaded,
    error: countryError,
    data: countryData,
  } = useQueryWithStore({
    type: 'getList',
    resource: 'Country',
    payload: {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'DESC' },
    },
  });

  const {
    loaded: localeLoaded,
    error: localeError,
    data: localeData,
  } = useQueryWithStore({
    type: 'getList',
    resource: 'Locale',
    payload: {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'DESC' },
    },
  });

  return isSuperAdmin(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Edit
        undoable={false}
        title={'resources.Region.fields.titles.edit'}
        {...props}
      >
        <SimpleForm>
          <TextInput source="name" validate={required()} />
          {countryData ? (
            <SelectArrayInput
              source="countriesIds"
              disabled={!(countryLoaded && !countryError)}
              label={'resources.Region.fields.countries'}
              fullWidth
              options={{
                error: countryLoaded && countryError ? true : false,
              }}
              className={classes.input}
              helperText={!localeError ? true : false}
              validate={required()}
              choices={countryData}
            />
          ) : (
            <LoadingComponent
              label={translate('resources.Region.fields.countries') + ' *'}
            />
          )}
          {countryLoaded && countryError ? (
            <ErrorText
              classes={classes}
              dataType="countries"
              translate={translate}
            />
          ) : null}
          {localeData ? (
            <SelectArrayInput
              source="localesIds"
              disabled={!(localeLoaded && !localeError)}
              label={'resources.Region.fields.locales'}
              fullWidth
              options={{
                error: localeLoaded && localeError ? true : false,
              }}
              className={classes.input}
              helperText={!localeError ? true : false}
              validate={required()}
              choices={localeData}
            />
          ) : (
            <LoadingComponent
              label={translate('resources.Region.fields.locales') + ' *'}
            />
          )}
          {localeLoaded && localeError ? (
            <ErrorText
              classes={classes}
              dataType="locales"
              translate={translate}
            />
          ) : null}
        </SimpleForm>
      </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

export default RegionEdit;
